import useDeleteRegistration from "@/hooks/useDeleteRegistration";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, Typography } from "@mui/material"
import React, { useEffect, useState } from "react";
import { DeleteRegistrationResponse, Registration, ResponseStatus } from "../../api/dtos";
import Loading from "./Loading";

interface IDeleteModalProps {
    registration?: Registration | null;
    handleModalClose?: (refetch: boolean) => void;
}

const DeleteModal: React.FC<IDeleteModalProps> = ({registration, handleModalClose}) => {

    const {deleteRegistration, pending} = useDeleteRegistration(registration?.id);
    const [deleteAttempted, setDeleteAttempted] = useState<boolean>(false);
    const [deletingRego, setDeletingRego] = useState<Registration | undefined>();
    const [response, setResponse] = useState<DeleteRegistrationResponse>();

    const deleteSuccess = !pending && deleteAttempted && !response?.responseStatus?.message;

    const handleClose = () => {
        if(handleModalClose){
            setDeleteAttempted(false);
            setDeletingRego(undefined);
            handleModalClose(deleteSuccess);
        }
    }

    const handleDelete = async () => {
        const resp = await deleteRegistration(registration?.id);
        if(response){
            setResponse(resp);
        }
    }

    useEffect(() => {
        return () => {
            setDeleteAttempted(false);
            setDeletingRego(undefined);
        }
    }, [])

    useEffect(() => {
        if(registration){
            setDeletingRego(registration);
        }
    }, [registration])

    useEffect(() => {
        if(!deleteAttempted && pending){
            setDeleteAttempted(true);
        }
    }, [pending])

    return (
        <Box>
            {
                pending && <Loading />
            }
            <Dialog 
                open={!!registration}
                onClose={handleClose}
                fullWidth={true}
                maxWidth="md"
            >
                <Box
                    sx={{
                        p: 2
                    }}
                >
                    {
                        deletingRego && (
                            <>
                                <Typography pl='22px' pt={2}
                                    variant="h4"
                                >
                                    Delete registration for {deletingRego?.invoiceNumber}
                                </Typography>
                                <DialogContent>
                                    {
                                        deleteAttempted ? 
                                            <>
                                            <DialogContentText>
                                                {
                                                    pending ? 
                                                    <Loading />
                                                    :
                                                    deleteSuccess ? "Registration deleted successfully" : "Could not delete registration"
                                                }
                                            </DialogContentText>
                                            </>
                                        :
                                        <DialogContentText>
                                            Confirm you would like to delete the registration for <strong>{deletingRego?.firstName} {deletingRego?.lastName} ({deletingRego?.invoiceNumber})</strong>
                                        </DialogContentText>
                                    }
                                </DialogContent>
                                <DialogActions>
                                    {
                                        !pending && !deleteSuccess && (
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                size="large"
                                                sx={{
                                                    width: {
                                                        xs: '100%',
                                                        md: 'fit-content'
                                                    },
                                                    minWidth: '160px',
                                                    border: 'solid',
                                                    borderColor: 'secondary.main',
                                                    borderRadius: '20px',
                                                    '&:hover': {
                                                        backgroundColor: 'primary.main',
                                                        borderColor: 'primary.main'
                                                    }
                                                }}
                                                onClick={handleDelete}
                                            >
                                                <Typography
                                                    variant="body2"
                                                >   
                                                    Confirm
                                                </Typography>
                                            </Button>
                                        )
                                            
                                    }
                                    
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        size="large"
                                        onClick={handleClose}
                                        sx={{
                                            width: {
                                                xs: '100%',
                                                md: 'fit-content'
                                            },
                                            minWidth: '160px',
                                            color: 'seconday.main',
                                            borderRadius: '20px',
                                            borderColor: 'seconday.main',
                                            '&:hover': {
                                                color: 'primary.main',
                                                borderColor: 'primary.main'
                                            }
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                        >   
                                        {
                                            deleteSuccess ?
                                                "Back"
                                                :
                                                "Cancel"
                                        }
                                        </Typography>
                                    </Button>
                                </DialogActions>
                            </>
                        )
                    }
                </Box>
            </Dialog>
        </Box>
    );
}

export default DeleteModal;